import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, MessageSquare, Trash2, Camera, Activity, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import ImagePicker, { absolutizeUrl } from "@/components/ImagePicker";
import ShareTrailStatusButton from "@/components/ShareTrailStatusButton";
import { tryNativeShare, formatConditionLine, shortAgo } from "@/lib/share";

const CONDITION_KEYS = ["perfect", "dry", "dusty", "humid", "wet", "mud", "leaves", "overgrown", "broken"];

const CONDITION_COLOR = {
    perfect: "text-emerald-300 border-emerald-300/60 bg-emerald-400/10",
    dry: "text-emerald-300 border-emerald-300/40 bg-emerald-400/5",
    dusty: "text-amber-300 border-amber-300/40 bg-amber-400/5",
    humid: "text-sky-300 border-sky-300/40 bg-sky-400/5",
    wet: "text-sky-400 border-sky-400/50 bg-sky-400/10",
    mud: "text-orange-400 border-orange-400/50 bg-orange-400/10",
    leaves: "text-amber-400 border-amber-400/50 bg-amber-400/10",
    overgrown: "text-amber-500 border-amber-500/50 bg-amber-500/10",
    broken: "text-abnormal-red border-abnormal-red/60 bg-abnormal-red/10",
};

function timeAgo(iso, t, lang) {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    const min = Math.floor(diff / 60);
    const hr = Math.floor(diff / 3600);
    const day = Math.floor(diff / 86400);
    if (day >= 7) return new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short" });
    if (day >= 1) return `${day}${lang === "it" ? "g" : "d"} ${t("reports.ago")}`;
    if (hr >= 1) return `${hr}h ${t("reports.ago")}`;
    if (min >= 1) return `${min}m ${t("reports.ago")}`;
    return lang === "it" ? "ora" : "now";
}

function Stars({ value, onChange, readOnly = false, size = 16 }) {
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    disabled={readOnly}
                    data-testid={`star-${n}`}
                    onClick={() => onChange && onChange(n)}
                    className={`${readOnly ? "cursor-default" : "hover:scale-110"} transition-transform`}
                    aria-label={`${n} stars`}
                >
                    <Star size={size} className={n <= value ? "text-amber-400 fill-amber-400" : "text-zinc-700"} />
                </button>
            ))}
        </div>
    );
}

function NewReportForm({ trailSlug, trailName, onCreated, t, lang }) {
    const [condition, setCondition] = useState("dry");
    const [rating, setRating] = useState(4);
    const [comment, setComment] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [busy, setBusy] = useState(false);

    const buildShareText = (report) => {
        const conditionLabel = t(`reports.conditions.${report.condition}`);
        const when = shortAgo(report.created_at, lang);
        const displayName = trailName || trailSlug;
        const line = formatConditionLine({
            name: displayName,
            conditionLabel,
            rating: report.rating,
            userName: report.user_name,
            when,
            dot: "›",
        });
        const note = report.comment ? `\n"${report.comment}"` : "";
        return `🚵 ${t("reports.shareTitleSingle").replace("{name}", String(displayName).toUpperCase())}\n${line}${note}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post(`/trails/${trailSlug}/reports`, {
                condition,
                rating,
                comment,
                image_url: imageUrl,
            });
            toast.success(t("reports.published"));
            setComment("");
            setImageUrl("");
            onCreated(r.data);

            // After publishing: immediately offer native share (WhatsApp / IG / FB).
            // If native picker unavailable (desktop), prompt to copy a ready-made
            // message via the standard confirm dialog.
            try {
                const text = buildShareText(r.data);
                const url = `${window.location.origin}/trail/${trailSlug}`;
                const result = await tryNativeShare({
                    title: t("reports.shareTitleSingle").replace("{name}", String(trailName || trailSlug).toUpperCase()),
                    text,
                    url,
                });
                if (!result.used) {
                    // Desktop fallback: copy to clipboard
                    if (window.confirm(`${t("reports.shareCopyConfirm")}\n\n${text}\n\n${url}`)) {
                        await navigator.clipboard.writeText(`${text}\n\n${url}`);
                        toast.success(t("reports.shareCopied"));
                    }
                }
            } catch (_e) { /* user cancelled share */ }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form
            data-testid="trail-report-form"
            onSubmit={submit}
            className="border border-white/10 bg-zinc-950 p-5 md:p-6 space-y-5"
        >
            <h3 className="font-display text-2xl uppercase">{t("reports.formTitle")}</h3>

            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">{t("reports.condition")}</p>
                <div className="flex flex-wrap gap-2">
                    {CONDITION_KEYS.map((c) => (
                        <button
                            type="button"
                            key={c}
                            data-testid={`report-cond-${c}`}
                            onClick={() => setCondition(c)}
                            className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 border transition-colors ${condition === c ? `${CONDITION_COLOR[c]} ring-1 ring-white/20` : "border-zinc-700 text-zinc-400 hover:border-white"}`}
                        >
                            {t(`reports.conditions.${c}`)}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">{t("reports.rating")}</p>
                <Stars value={rating} onChange={setRating} size={22} />
            </div>

            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">{t("reports.comment")}</p>
                <textarea
                    maxLength={400}
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t("reports.commentPh")}
                    data-testid="report-comment"
                    className="w-full bg-black border border-white/10 text-white px-3 py-2 focus:border-abnormal-acid outline-none"
                />
            </div>

            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 flex items-center gap-2">
                    <Camera size={12} /> {t("reports.photo")}
                </p>
                <ImagePicker value={imageUrl} onChange={setImageUrl} testidPrefix="report-photo" />
            </div>

            <button
                type="submit"
                disabled={busy}
                data-testid="report-submit"
                className="bg-abnormal-acid text-black uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-abnormal-paper hover:text-black transition-colors disabled:opacity-50"
            >
                {busy ? t("reports.submitting") : t("reports.submit")}
            </button>
        </form>
    );
}

export default function TrailReports({ trailSlug, trailName }) {
    const { t, lang } = useLang();
    const { user } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/trails/${trailSlug}/reports`);
            setReports(r.data || []);
        } catch (_e) {
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (trailSlug) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trailSlug]);

    const latest = useMemo(() => (reports.length ? reports[0] : null), [reports]);

    const onDelete = async (id) => {
        if (!window.confirm(t("reports.confirmDelete"))) return;
        try {
            await api.delete(`/trails/${trailSlug}/reports/${id}`);
            toast.success(t("reports.deleted"));
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    /** Share an individual report (any from the list, not just the latest). */
    const shareReport = async (r) => {
        const displayName = trailName || trailSlug;
        const conditionLabel = t(`reports.conditions.${r.condition}`);
        const when = shortAgo(r.created_at, lang);
        const line = formatConditionLine({
            name: displayName,
            conditionLabel,
            rating: r.rating,
            userName: r.user_name,
            when,
            dot: "›",
        });
        const note = r.comment ? `\n"${r.comment}"` : "";
        const text = `🚵 ${t("reports.shareTitleSingle").replace("{name}", String(displayName).toUpperCase())}\n${line}${note}`;
        const url = `${window.location.origin}/trail/${trailSlug}`;
        try {
            const result = await tryNativeShare({ title: displayName, text, url });
            if (!result.used) {
                if (window.confirm(`${t("reports.shareCopyConfirm")}\n\n${text}\n\n${url}`)) {
                    await navigator.clipboard.writeText(`${text}\n\n${url}`);
                    toast.success(t("reports.shareCopied"));
                }
            }
        } catch (_e) { /* cancelled */ }
    };

    return (
        <section data-testid="trail-reports-section" className="mt-12">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-abnormal-acid font-bold flex items-center gap-2">
                        <Activity size={14} /> {t("reports.eyebrow")}
                    </p>
                    <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight mt-1">
                        {t("reports.title")}
                    </h2>
                </div>
                {latest && (
                    <div className="flex flex-col items-stretch md:items-end gap-2">
                        <div data-testid="trail-current-status" className={`px-4 py-2 border-2 ${CONDITION_COLOR[latest.condition] || "border-zinc-700"}`}>
                            <p className="text-[10px] uppercase tracking-[0.3em] opacity-70 font-bold">{t("reports.currentStatus")}</p>
                            <p className="font-display text-xl uppercase mt-0.5">
                                {t(`reports.conditions.${latest.condition}`)} · {latest.rating}/5
                            </p>
                            <p className="text-[10px] uppercase tracking-wider opacity-60 mt-0.5">
                                {timeAgo(latest.created_at, t, lang)} · @{latest.user_name}
                            </p>
                        </div>
                        <ShareTrailStatusButton
                            trailName={trailName || trailSlug}
                            trailSlug={trailSlug}
                            latestReport={latest}
                        />
                    </div>
                )}
            </div>

            {user ? (
                <div className="mt-6 space-y-3">
                    <div data-testid="report-cta-banner" className="border-l-4 border-abnormal-acid bg-abnormal-charcoal text-abnormal-paper px-5 py-4 flex items-start gap-4">
                        <Activity size={22} className="flex-shrink-0 mt-0.5 text-abnormal-acid" />
                        <div className="min-w-0">
                            <p className="font-display text-xl md:text-2xl uppercase leading-tight">{t("reports.ctaBanner")}</p>
                            <p className="text-xs md:text-sm opacity-90 mt-1">{t("reports.ctaBannerSub")}</p>
                        </div>
                    </div>
                    <NewReportForm trailSlug={trailSlug} trailName={trailName} onCreated={() => load()} t={t} lang={lang} />
                </div>
            ) : (
                <div className="mt-6">
                    <div data-testid="report-cta-banner-guest" className="border-l-4 border-abnormal-acid bg-abnormal-charcoal text-abnormal-paper px-5 py-5">
                        <p className="font-display text-xl md:text-2xl uppercase leading-tight inline-flex items-center gap-3">
                            <Activity size={22} className="text-abnormal-acid" /> {t("reports.ctaBanner")}
                        </p>
                        <p className="text-sm opacity-90 mt-1">{t("reports.loginRequired")}</p>
                        <Link
                            to="/login"
                            className="btn-primary mt-3 !py-2"
                            data-testid="report-cta-login"
                        >
                            {t("reports.loginCta")}
                        </Link>
                    </div>
                </div>
            )}

            <div className="mt-8 space-y-3">
                {loading ? (
                    <p className="text-zinc-500 text-sm uppercase tracking-wider">…</p>
                ) : reports.length === 0 ? (
                    <p data-testid="reports-empty" className="text-zinc-500 uppercase tracking-wider text-sm border border-dashed border-white/10 p-6 text-center">
                        {t("reports.empty")}
                    </p>
                ) : (
                    reports.map((r) => (
                        <article
                            key={r.id}
                            data-testid={`trail-report-${r.id}`}
                            className="border border-white/10 bg-zinc-950 p-5 flex flex-col md:flex-row gap-4"
                        >
                            {r.image_url && (
                                <div className="md:w-44 flex-shrink-0">
                                    <img
                                        src={absolutizeUrl(r.image_url)}
                                        alt=""
                                        className="w-full h-32 md:h-32 object-cover border border-white/10"
                                    />
                                </div>
                            )}
                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className={`text-[11px] uppercase tracking-[0.2em] font-bold px-2 py-1 border ${CONDITION_COLOR[r.condition] || "border-zinc-700"}`}>
                                            {t(`reports.conditions.${r.condition}`)}
                                        </span>
                                        <Stars value={r.rating} readOnly size={14} />
                                    </div>
                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{timeAgo(r.created_at, t, lang)}</span>
                                </div>
                                {r.comment && (
                                    <p className="text-zinc-200 mt-3 leading-relaxed flex gap-2">
                                        <MessageSquare size={14} className="text-zinc-500 mt-1 flex-shrink-0" />
                                        <span>{r.comment}</span>
                                    </p>
                                )}
                                <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-white/10">
                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">@{r.user_name}</span>
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => shareReport(r)}
                                            data-testid={`report-share-${r.id}`}
                                            className="text-[10px] uppercase tracking-wider text-abnormal-acid hover:text-white flex items-center gap-1 font-bold"
                                        >
                                            <Share2 size={11} /> {t("reports.share")}
                                        </button>
                                        {user && (user.id === r.user_id || user.role === "admin") && (
                                            <button
                                                type="button"
                                                onClick={() => onDelete(r.id)}
                                                data-testid={`report-delete-${r.id}`}
                                                className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-abnormal-acid flex items-center gap-1"
                                            >
                                                <Trash2 size={11} /> {t("reports.delete")}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))
                )}
            </div>
        </section>
    );
}
